/**
 * 手抄自 C02 的 typed REST client。
 *
 * 設計重點：
 * 1. **Idempotency-Key**：C02 §0 規定所有 POST 接受、出手**必須**帶。這裡由 client 統一產生。
 * 2. **Clock skew**：對局倒數是對著伺服器的 `deadline_at` 算的。瀏覽器時鐘可能歪掉幾秒，
 *    倒數就會不準。我們從每個 REST 回應的 `Date` header（CORS-safelisted，跨網域也讀得到）
 *    估算 skew，之後所有倒數都用 `serverNow()`。
 * 3. **不吞錯**：非 2xx 一律丟 `ApiError`，帶上 C02 §0 的 `error.code`，讓 UI 可以針對
 *    `ROUND_MISMATCH` / `ALREADY_SUBMITTED` 做正確處理。
 * 4. **Cookie 只走換發那一條路**（C03 §5.1）：全站唯一會送出 cookie 的請求是
 *    `POST /auth/guest`，其餘一律 `credentials: 'omit'`。理由見 `RequestOptions.withCookies`。
 */

import type {
  ApiErrorBody,
  CancelRematchResponse,
  GameDetail,
  GamesResponse,
  GuestTokenResponse,
  InviteResponse,
  JoinResponse,
  LeaderboardRow,
  LobbyResponse,
  MatchView,
  Paged,
  QueueRequest,
  QueueTicketCreated,
  QueueTicketStatus,
  RematchResponse,
  ReplayResponse,
  SubmitActionRequest,
  SubmitActionResponse,
} from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: Record<string, unknown>;
  readonly retryAfterMs: number | null;

  constructor(status: number, code: string, message: string, detail: Record<string, unknown> = {}, retryAfterMs: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.retryAfterMs = retryAfterMs;
  }

  /** 網路層失敗（DNS/離線/CORS），不是伺服器回的錯誤。 */
  static network(cause: unknown): ApiError {
    const msg = cause instanceof Error ? cause.message : String(cause);
    return new ApiError(0, 'NETWORK_ERROR', msg);
  }
}

export type TokenSupplier = () => string | null;

export interface ApiClientOptions {
  baseUrl: string;
  getToken?: TokenSupplier;
  fetchImpl?: typeof fetch;
  /** 產生 Idempotency-Key，測試時可注入確定性版本。 */
  newIdempotencyKey?: () => string;
  /**
   * 換發匿名身分時要不要送出 cookie（C03 §5.1）。預設 `true` —— 那是契約的目標狀態。
   *
   * 設 `false` 會讓 `POST /auth/guest` 退回 `credentials: 'omit'`，也就是落地前的行為
   * （每次換發都是一個全新的匿名身分）。呼叫端由 `config.guestPersistence` 決定，
   * 為什麼需要這個開關寫在 `config.ts`。
   */
  guestCookieExchange?: boolean;
}

export function randomIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // 退路：不需要密碼學強度，只要夠唯一以避免重試撞號。
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** 某些端點（如 GET /games）是公開的，不需要也不該送 token。 */
  anonymous?: boolean;
  /**
   * ★ 這個請求要不要帶上瀏覽器的 cookie（C03 §5.1）。**全站只有 `POST /auth/guest` 設它。**
   *
   * ## 為什麼不是「整個 client 一律 include」
   *
   * C03 §5.1 給 cookie 的定義是「一個**只能用來換發**的憑證，不能直接當 `Authorization` 用」。
   * 那句話要在**程式的形狀**上成立，不能只寫在文件裡：若每個請求都送 cookie，
   * 那麼每一個變更狀態的端點都變成「可能被 cookie 認證」的端點，
   * 於是 ambient authority 回來了，CSRF 也跟著回來了 —— 而 cookie 原本只是要回答
   * 「我是誰」這一個問題。
   *
   * 把 `include` 限縮在換發那一個請求上，等於用型別與呼叫點把契約釘住：
   *   * 其餘所有請求維持 `omit` ⇒ 檔頭第 4 點那句「天然免疫 CSRF」對它們仍然成立；
   *   * 跨來源帶憑證的 CORS 需求（`Access-Control-Allow-Credentials`）也只落在一個端點上。
   *
   * 換發端點自己的 CSRF 風險：`SameSite=Lax` 本來就擋掉跨站 POST 送 cookie；
   * 就算被強迫發出，攻擊者也讀不到跨來源的回應，拿不到那張 token。
   */
  withCookies?: boolean;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenSupplier;
  private readonly fetchImpl: typeof fetch;
  private readonly newIdempotencyKey: () => string;
  private readonly guestCookieExchange: boolean;
  /** serverNow() - Date.now()，由回應的 Date header 估算。 */
  private clockSkewMs = 0;
  private clockSkewSamples = 0;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.getToken = opts.getToken ?? (() => null);
    this.fetchImpl = opts.fetchImpl ?? ((...args) => globalThis.fetch(...args));
    this.newIdempotencyKey = opts.newIdempotencyKey ?? randomIdempotencyKey;
    this.guestCookieExchange = opts.guestCookieExchange ?? true;
  }

  /** 以伺服器時鐘為準的「現在」。倒數計時一律用這個，不要用 Date.now()。 */
  serverNow(): number {
    return Date.now() + this.clockSkewMs;
  }

  get skewMs(): number {
    return this.clockSkewMs;
  }

  private observeServerDate(res: Response): void {
    const raw = res.headers.get('date');
    if (!raw) return;
    const serverMs = Date.parse(raw);
    if (Number.isNaN(serverMs)) return;
    // Date header 只有秒級精度，取移動平均以免倒數抖動。
    const sample = serverMs - Date.now();
    this.clockSkewSamples += 1;
    const n = Math.min(this.clockSkewSamples, 10);
    this.clockSkewMs = this.clockSkewMs + (sample - this.clockSkewMs) / n;
  }

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const method = opts.method ?? 'GET';
    const headers: Record<string, string> = { Accept: 'application/json' };

    if (!opts.anonymous) {
      const token = this.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET' && method !== 'DELETE') {
      headers['Idempotency-Key'] = opts.idempotencyKey ?? this.newIdempotencyKey();
    }

    // 預設 `omit`：token 走 Authorization header，不用 cookie → 天然免疫 CSRF。
    // 唯一的例外是 C03 §5.1 的換發端點（`withCookies`），它要拿 httpOnly cookie 換 token。
    const credentials: RequestCredentials =
      opts.withCookies && this.guestCookieExchange ? 'include' : 'omit';

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
        credentials,
        mode: 'cors',
      });
    } catch (cause) {
      throw ApiError.network(cause);
    }

    this.observeServerDate(res);

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
    }

    if (!res.ok) {
      const body = parsed as ApiErrorBody | null;
      const retryAfter = res.headers.get('retry-after');
      throw new ApiError(
        res.status,
        body?.error?.code ?? `HTTP_${res.status}`,
        body?.error?.message ?? res.statusText ?? 'request failed',
        body?.error?.detail ?? {},
        retryAfter ? Number(retryAfter) * 1000 : null,
      );
    }

    return parsed as T;
  }

  // --- C02 §2 公開端點 ---------------------------------------------------

  listGames(signal?: AbortSignal): Promise<GamesResponse> {
    return this.request<GamesResponse>('/games', { anonymous: true, signal });
  }

  getGame(gameKey: string, signal?: AbortSignal): Promise<GameDetail> {
    return this.request<GameDetail>(`/games/${encodeURIComponent(gameKey)}`, { anonymous: true, signal });
  }

  getLeaderboard(
    gameKey: string,
    params: { variant_key?: string; window?: '24h' | '7d' | 'all' } = {},
    signal?: AbortSignal,
  ): Promise<Paged<LeaderboardRow>> {
    const qs = new URLSearchParams();
    if (params.variant_key) qs.set('variant_key', params.variant_key);
    if (params.window) qs.set('window', params.window);
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.request<Paged<LeaderboardRow>>(`/games/${encodeURIComponent(gameKey)}/leaderboard${suffix}`, {
      anonymous: true,
      signal,
    });
  }

  // --- C02 §3 認證 -------------------------------------------------------

  /**
   * C03 §5.1 的**換發**端點 —— 全站唯一帶 cookie 的請求。
   *
   *     httpOnly cookie（JS 讀不到）  ──▶  POST /auth/guest  ──▶  短命 bearer token（記憶體）
   *
   * `anonymous: true` 與 `withCookies: true` 同時成立不是矛盾：這裡的「anonymous」
   * 指的是**不送 Authorization header**（此刻手上還沒有 token，這正是來換一張的原因），
   * 而 cookie 帶的是「我是誰」。兩者是不同的憑證，用途也不同。
   */
  createGuest(displayName?: string, newIdentity = false): Promise<GuestTokenResponse> {
    return this.request<GuestTokenResponse>('/auth/guest', {
      method: 'POST',
      anonymous: !newIdentity,
      withCookies: true,
      body: { ...(displayName ? { display_name: displayName } : {}), ...(newIdentity ? { new_identity: true } : {}) },
    });
  }

  endGuest(): Promise<void> {
    return this.request<void>('/auth/guest', { method: 'DELETE', withCookies: true });
  }

  whoami(): Promise<{ subject_id: string; realm: string }> {
    return this.request('/auth/whoami');
  }

  // --- C02 §4 大廳 -------------------------------------------------------

  getLobby(gameKey: string, signal?: AbortSignal): Promise<LobbyResponse> {
    return this.request<LobbyResponse>(`/lobby/${encodeURIComponent(gameKey)}`, { signal });
  }

  enqueue(gameKey: string, req: QueueRequest): Promise<QueueTicketCreated> {
    return this.request<QueueTicketCreated>(`/lobby/${encodeURIComponent(gameKey)}/queue`, {
      method: 'POST',
      body: req,
    });
  }

  cancelQueue(ticketId: string): Promise<void> {
    return this.request<void>(`/lobby/queue/${encodeURIComponent(ticketId)}`, { method: 'DELETE' });
  }

  getTicket(ticketId: string, signal?: AbortSignal): Promise<QueueTicketStatus> {
    return this.request<QueueTicketStatus>(`/lobby/queue/${encodeURIComponent(ticketId)}`, { signal });
  }

  createInvite(gameKey: string, variantKey: string): Promise<InviteResponse> {
    return this.request<InviteResponse>(`/lobby/${encodeURIComponent(gameKey)}/invite`, {
      method: 'POST',
      body: { variant_key: variantKey },
    });
  }

  joinByCode(inviteCode: string): Promise<JoinResponse> {
    return this.request<JoinResponse>(`/lobby/join/${encodeURIComponent(inviteCode)}`, { method: 'POST', body: {} });
  }

  // --- C02 §5 對局 -------------------------------------------------------

  getMatch(matchId: string, signal?: AbortSignal): Promise<MatchView> {
    return this.request<MatchView>(`/matches/${encodeURIComponent(matchId)}`, { signal });
  }

  /** 出手。C02 §0：**必須**帶 Idempotency-Key，避免網路重試造成重複出手。 */
  submitAction(matchId: string, req: SubmitActionRequest, idempotencyKey: string): Promise<SubmitActionResponse> {
    return this.request<SubmitActionResponse>(`/matches/${encodeURIComponent(matchId)}/actions`, {
      method: 'POST',
      body: req,
      idempotencyKey,
    });
  }

  forfeit(matchId: string): Promise<void> {
    return this.request<void>(`/matches/${encodeURIComponent(matchId)}/forfeit`, { method: 'POST', body: {} });
  }

  rematch(matchId: string): Promise<RematchResponse> {
    return this.request<RematchResponse>(`/matches/${encodeURIComponent(matchId)}/rematch`, { method: 'POST', body: {} });
  }

  /**
   * 撤回**自己**的再戰意願（C02 §5 v1.7 的 `DELETE /matches/{id}/rematch`）。
   *
   * ## 為什麼這是安全性修正，不是一顆 UI 按鈕
   *
   * 在這支端點存在之前**沒有任何途徑**可以撤回意願：按下再戰之後「回大廳」
   * 只是換頁，意願仍在伺服器上活著 120 秒。對手在那段時間按下，系統就會替兩人
   * 建立新對局，而其中一人**人在大廳** —— 30 秒後 `abandon_no_show()`
   * 判他「對手沒出現」而**記一場敗仗**（實測確認）。
   * 也就是說：一個玩家會因為「按了再戰又改變主意」而輸掉一場他不知道存在的對局。
   *
   * ⚠️ 呼叫端要處理的兩個非 200 出口（都不是「請再試一次」）：
   *  * `409 REMATCH_ALREADY_MATCHED` —— **不是錯誤，是導頁指令**，
   *    `detail.new_match_id` 是那一場真的已經開好的對局。
   *  * `404 NOT_FOUND` —— 不是該局座位（比照 `cancelQueue`：**不是 403**，
   *    可區分的錯誤碼等於一個「這場對局有誰」的探測器）。
   *
   * 沒有意願可撤時**照樣回 200**（冪等），所以呼叫端不必先問「我按過了嗎」。
   * 與 `cancelQueue()` 一樣是 DELETE ⇒ `request()` 不會補 `Idempotency-Key`
   * （C02 §0 只規範 POST；DELETE 本身就是冪等的）。
   */
  cancelRematch(matchId: string): Promise<CancelRematchResponse> {
    return this.request<CancelRematchResponse>(`/matches/${encodeURIComponent(matchId)}/rematch`, {
      method: 'DELETE',
    });
  }

  getReplay(matchId: string, signal?: AbortSignal): Promise<ReplayResponse> {
    return this.request<ReplayResponse>(`/matches/${encodeURIComponent(matchId)}/replay`, { signal });
  }
}
